import loadable from "@loadable/component"
import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"

import { setExperiment } from "app-store-v2/app/actions"
import AsSeenIn from "areas/landing/elements/landing-as-seen-in"
import fetchProduct from "areas/product/hoc/fetch-product"
import withImages from "areas/product/hoc/with-images"
import SimpleDeferAfterLoadWrapper from "components/common/simple-defer-load-wrapper"
import Media from "components/ui/media"
import { IS_JEWLR, retry } from "helpers/application"
import fetchWishlist from "hoc/fetch-wishlist"

import ImageAreaContent from "./image-area-content"
import ProductDescription from "./info/product-description"
import MainBody from "./main-body"
import Breadcrumbs from "./wizard-header/breadcrumbs"
import OutOfStockBanner from "./wizard-header/out-of-stock-banner"

const GenericNotFound = loadable(() =>
  retry(() => import("app/generic-not-found"))
)
const ProductFooter = loadable(() => retry(() => import("./product-footer")), {
  ssr: false,
})
const CartPopup = loadable(() => retry(() => import("areas/cart/popup")), {
  ssr: false,
})
const HonestPricingModal = loadable(() =>
  retry(() => import("./modals/honest-pricing-modal"), {
    ssr: false,
  })
)
const ReturnPolicyModal = loadable(() =>
  retry(() => import("./modals/return-policy-modal"), {
    ssr: false,
  })
)
const PreviewImageModal = loadable(() =>
  retry(() => import("./modals/preview-image-modal"), {
    ssr: false,
  })
)
const Thumbnails = loadable(() => retry(() => import("./thumbnails")))
// const Miniview = loadable(() => retry(() => import("./miniview")))
// const J3DViewHalfModalsWrapper = loadable(() =>
//   retry(() => import("areas/product/jewlr/modals/j3dview-half-modals-wrapper"))
// )

if (typeof window != "undefined") {
  try {
    if (window.location.hash != "") {
      var hash = window.location.hash.split("#")[1]
      if (hash.match("&gid=[0-9]*&pid=[0-9]*")) {
        window.location.hash = ""
      }
    }
  } catch (e) {
    console.log(e)
  }

  // EDGE ONLY - remove this line once Microsoft Edge decides to start supporting
  // HTMLCanvasElement.toBlob
  try {
    if (
      !Object.prototype.hasOwnProperty.call(
        HTMLCanvasElement.prototype,
        "toBlob"
      )
    ) {
      require("blueimp-canvas-to-blob")
    }
  } catch (e) {
    // Continue if this polyfill fails to load
    console.log("Couldn't load toBlob polyfill", e)
  }
}

// let scrollTick = false

const selectHasTwoOrMoreStoneOrAccentOptions = createSelector(
  [(state) => state.wizard.prefOrder],
  (prefOrder) =>
    prefOrder.reduce(
      (count, pref) => count + (isStoneOrAccentPref(pref) ? 1 : 0),
      0
    ) >= 2
)

export const isStoneOrAccentPref = (pref) =>
  /^s\d+$/.test(pref) || pref === "as"

export const makeShouldUseNewWizardExp = () => {
  const guidedStoneSelectorExp = useSelector(
    (state) => state.experiment.guidedStoneSelector.variant_id
  )
  const hasTwoOrMoreStoneOrAccentOptions = useSelector(
    selectHasTwoOrMoreStoneOrAccentOptions
  )

  return guidedStoneSelectorExp === 1 && hasTwoOrMoreStoneOrAccentOptions
}

/**
 * Product index
 * @param {object} data - default data from _react.html
 */
const InternalProduct = ({
  currentSlideIndex,
  j3dSlideIndex,
  // miniview,
  // toggleMiniview,
  // toggleMiniviewTab,
  productTitle,
  slidesArray,
  staticContext,
  styleCode,
}) => {
  const imageAreaRef = useRef()
  const outOfStockRef = useRef()
  const dispatch = useDispatch()
  const outOfStock = useSelector(
    (state) => state.wizard.header?.product_out_of_stock
  )
  const productNotFound = useSelector((state) => state.wizardUI.productNotFound)
  // const hasDiamondSelector = useSelector(
  //   (state) => state.wizard.hasDiamondSelector
  // )
  const guidedStoneSelectorExp = useSelector(
    (state) => state.experiment.guidedStoneSelector
  )
  const hasTwoOrMoreStoneOrAccentOptions = useSelector(
    selectHasTwoOrMoreStoneOrAccentOptions
  )
  const pdpUgcExp = useSelector((state) => state.experiment.pdpUgc)
  // const wizardData = useSelector((state) => state.wizard.data)

  const ssrNotFound =
    staticContext && styleCode === undefined && outOfStock === undefined

  // const qualifiesForUnifiedStonePicker = Boolean(
  //   (wizardData?.o1 &&
  //     wizardData.o1.title?.toLowerCase().includes("number") &&
  //     Object.keys(wizardData).some((key) => /^s\d+$/.test(key))) ||
  //     wizardData?.sc
  // )

  // const requestScroll = () => {
  //   window.requestAnimationFrame(scrollListener)
  // }

  // const scrollListener = () => {
  //   if (!scrollTick) {
  //     scrollTick = true
  //     let scrollPos = window.scrollY

  //     if (scrollPos > 500) {
  //       if (!miniview.show) {
  //         if (!miniview.showTab) {
  //           toggleMiniviewTab(true)
  //         }
  //         if (!miniview.swipeHide) {
  //           toggleMiniview(true)
  //         }
  //       }
  //     } else {
  //       if (miniview.show) {
  //         toggleMiniview(false)
  //         toggleMiniviewTab(false)
  //       }
  //     }
  //     scrollTick = false
  //   }
  // }

  // useEffect(() => {
  //   if (unifiedStonePickerExp && qualifiesForUnifiedStonePicker) {
  //     dispatch(
  //       setExperiment({
  //         unifiedStonePicker: unifiedStonePickerExp,
  //       })
  //     )
  //   }
  // }, [unifiedStonePickerExp, qualifiesForUnifiedStonePicker])

  useEffect(() => {
    if (guidedStoneSelectorExp && hasTwoOrMoreStoneOrAccentOptions) {
      dispatch(setExperiment({ guidedStoneSelector: guidedStoneSelectorExp }))
    }
  }, [dispatch, guidedStoneSelectorExp, hasTwoOrMoreStoneOrAccentOptions])

  useEffect(() => {
    if (pdpUgcExp) {
      dispatch(setExperiment({ pdpUgc: pdpUgcExp }))
    }
  }, [dispatch, pdpUgcExp])

  useEffect(() => {
    document.body.classList.add("pdp")
    return () => document.body.classList.remove("pdp")
  }, [])

  if (productNotFound || ssrNotFound) {
    return <GenericNotFound staticContext={staticContext} />
  }

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable react/jsx-sort-props, react/no-unknown-property */}
        <meta content="240" name="og_image_width" property="og:image:width" />
        <meta content="240" name="og_image_height" property="og:image:height" />
        {slidesArray?.length > 0 && slidesArray[0].src && (
          <meta
            content={slidesArray[0].src}
            name="og_image"
            property="og:image"
          />
        )}
        {slidesArray?.length > 0 && slidesArray[0].srcset && (
          <link
            rel="preload"
            fetchpriority="high"
            as="image"
            imagesrcset={slidesArray[0].srcset}
            imagesizes={slidesArray[0].sizes}
          />
        )}
        {/* eslint-enable react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <div className="tablet:px-16 tablet:mb-10 tablet:pt-32 tablet:max-w-835 desktop:max-w-full min-[1568px]:!max-w-1440 mx-auto pt-8 [--page-padding-y:32px]">
        {outOfStock && (
          <Media className="my-4 px-20" lessThan="tablet">
            <OutOfStockBanner outOfStockRef={outOfStockRef} />
          </Media>
        )}
        <Media className="px-16" lessThan="tablet">
          <Breadcrumbs />
        </Media>
        <div
          className="tablet:items-start tablet:flex-row desktop:gap-40 flex flex-col"
          style={{
            "--sticky-top":
              "calc(var(--total-header-height) + var(--page-padding-y))",
          }}
        >
          <Media
            className="tablet:sticky tablet:top-(--sticky-top) basis-52 [grid-area:thumbnails]"
            greaterThanOrEqual="desktop"
          >
            <Thumbnails
              currentSlideIndex={currentSlideIndex}
              j3dSlideIndex={j3dSlideIndex}
              productTitle={productTitle}
              slidesArray={slidesArray}
              styleCode={styleCode}
            />
          </Media>
          <div
            className="tablet:w-1/2 tablet:sticky tablet:top-(--sticky-top) tablet:mb-0 relative mb-40 flex-1"
            ref={imageAreaRef}
          >
            <ImageAreaContent />
            {/* {isMobile && <Miniview />} */}
            {/* <J3DViewHalfModalsWrapper /> */}
          </div>
          <div
            className="tablet:w-1/2 desktop:w-480 tablet:sticky tablet:top-(--sticky-top) w-full min-w-0 px-16 [grid-area:wizard]"
            data-ge-product-container
          >
            <Media greaterThanOrEqual="tablet">
              <Breadcrumbs />
            </Media>
            <MainBody
              imageAreaRef={imageAreaRef}
              outOfStockRef={outOfStockRef}
            />
          </div>
        </div>
      </div>
      <div className="desktop:max-w-1440 contained tablet:px-0 tablet:border-t tablet:border-t-grey-20">
        <ProductDescription />
      </div>
      {IS_JEWLR() && <AsSeenIn />}
      {IS_JEWLR() && <ProductFooter />}
      <SimpleDeferAfterLoadWrapper>
        <CartPopup />
        <PreviewImageModal />
        {IS_JEWLR() && <HonestPricingModal />}
        {IS_JEWLR() && <ReturnPolicyModal />}
      </SimpleDeferAfterLoadWrapper>
    </>
  )
}

InternalProduct.propTypes = {
  currentSlideIndex: PropTypes.number,
  j3dSlideIndex: PropTypes.number,
  // miniview: PropTypes.object,
  // toggleMiniview: PropTypes.func,
  // toggleMiniviewTab: PropTypes.func,
  productTitle: PropTypes.string,
  slidesArray: PropTypes.array,
  staticContext: PropTypes.object,
  styleCode: PropTypes.string,
  trackSlide: PropTypes.func,
  updateChoice: PropTypes.func,
}

const Product = fetchWishlist(InternalProduct)

export default fetchProduct(withImages(Product))
